<template>
  <div class="countdown-button-container">
    <van-button
      :type="type"
      :size="convertedSize"
      :block="blockButton"
      @click="handleButtonClick"
    >
      {{ currentButtonText }}
    </van-button>

    <van-popup
      v-model="showPopup"
      position="bottom"
      round
      :style="{ height: '90%' }"
      :close-on-click-overlay="false"
      :lock-scroll="true"
      safe-area-inset-bottom
    >
      <div class="popup-header">
        <span class="popup-title">阅读内容</span>
        <van-button
          class="close-button"
          type="default"
          size="small"
          :disabled="!canClose"
          @click="handleClose"
        >
          {{ closeButtonText }}
        </van-button>
      </div>
      <div class="popup-content">
        <iframe
          v-if="linkUrl"
          :src="linkUrl"
          frameborder="0"
          class="content-iframe"
        ></iframe>
        <div v-else class="no-content">
          暂无内容
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Button, Popup } from 'vant';

export default {
  name: 'CountDownTimeButton',
  model: {
    prop: 'value',
    event: 'input',
  },
  components: {
    'van-button': Button,
    'van-popup': Popup,
  },
  props: {
    // 组件值,表示是否已阅读,用于表单校验
    value: {
      type: Boolean,
      default: false,
    },
    // 按钮类型
    // 可选值: 'default' | 'primary' | 'info' | 'warning' | 'danger'
    // default: 默认按钮,白底黑字
    // primary: 主要按钮,蓝色背景
    // info: 信息按钮,灰蓝色背景
    // warning: 警告按钮,橙色背景
    // danger: 危险按钮,红色背景
    type: {
      type: String,
      default: 'primary',
    },
    // 按钮尺寸
    // 可选值: 'large' | 'normal' | 'small'
    // large: 对应 Vant 的 normal (44px)
    // normal: 对应 Vant 的 small (32px)
    // small: 对应 Vant 的 mini (24px)
    size: {
      type: String,
      default: 'normal',
    },
    // 按钮是否为块级元素
    blockButton: {
      type: Boolean,
      default: false,
    },
    // 弹窗内部停留时长(秒),倒计时结束后才能关闭弹窗
    stayTime: {
      type: [Number, String],
      default: 60,
    },
    // 按钮点击前的文本
    beforeClickText: {
      type: String,
      default: '点击阅读',
    },
    // 按钮点击后(已阅读)的文本
    afterClickText: {
      type: String,
      default: '已阅读',
    },
    // 超链接地址,在弹窗中通过 iframe 展示
    linkUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showPopup: false,
      countdown: 0,
      isCountingDown: false,
      isRead: false,
      timer: null,
    };
  },
  computed: {
    // 转换按钮尺寸
    // 传入参数: 'large' | 'normal' | 'small'
    // Vant 实际尺寸: 'normal' | 'small' | 'mini'
    // large -> normal, normal -> small, small -> mini
    convertedSize() {
      const sizeMap = {
        large: 'normal',
        normal: 'small',
        small: 'mini',
      };
      return sizeMap[this.size] || 'small';
    },
    currentButtonText() {
      return this.isRead ? this.afterClickText : this.beforeClickText;
    },
    canClose() {
      return !this.isCountingDown;
    },
    closeButtonText() {
      if (this.isCountingDown) {
        return this.formatTime(this.countdown);
      }
      return '关闭';
    },
  },
  watch: {
    value(newVal) {
      this.isRead = newVal;
    },
  },
  methods: {
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    handleButtonClick() {
      this.showPopup = true;
      if (!this.isRead) {
        this.startCountdown();
      }
    },
    startCountdown() {
      const initialTime = Number(this.stayTime);
      if (initialTime <= 0) {
        return;
      }

      this.countdown = initialTime;
      this.isCountingDown = true;
      this.clearTimer();

      this.timer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.stopCountdown();
        }
      }, 1000);
    },
    stopCountdown() {
      this.isCountingDown = false;
      this.countdown = 0;
      this.clearTimer();
    },
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    handleClose() {
      if (this.isCountingDown) {
        return;
      }
      this.showPopup = false;
      if (!this.isRead) {
        this.isRead = true;
        this.$emit('input', true);
        this.$emit('change', true);
      }
    },
  },
  beforeDestroy() {
    this.clearTimer();
  },
};
</script>

<style scoped>
.countdown-button-container {
  display: inline-block;
  width: 100%;
  -webkit-tap-highlight-color: transparent;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #ebedf0;
  background-color: #fff;
  position: sticky;
  top: 0;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.popup-title {
  font-size: 17px;
  font-weight: 600;
  color: #323233;
  line-height: 24px;
}

.close-button {
  min-width: 88px;
  height: 32px;
  border-radius: 16px;
  font-size: 14px;
}

.popup-content {
  height: calc(100% - 57px);
  overflow: auto;
  background-color: #f7f8fa;
  -webkit-overflow-scrolling: touch;
}

.content-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.no-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #969799;
  font-size: 15px;
  padding: 20px;
  text-align: center;
}

@media screen and (max-width: 375px) {
  .popup-title {
    font-size: 16px;
  }
  
  .close-button {
    min-width: 80px;
    height: 30px;
    font-size: 13px;
  }
}

@supports (bottom: env(safe-area-inset-bottom)) {
  .popup-content {
    padding-bottom: env(safe-area-inset-bottom);
  }
}
</style>
